import * as z from "zod";

// The fiscal numbers Nexxt gives the documents of a sale (spec v0.3 §7.4.3, C-48, ADR-255): one series for each
// company, type and year, whose prefix and format Finance keeps in Backstage (dataset `document_number_series`). The
// format is a pattern of tokens: the domain writes the number with it, the dataset checks it when Finance saves it.

/**
 * The documents Nexxt numbers itself: the invoices and the credit notes of a sale, of both companies. The proforma
 * (D-24), the credit notes of a supplier and every purchase document keep Business Central's number.
 */
export const numberedDocumentTypes = ["invoice", "credit_note"] as const;
export type NumberedDocumentType = (typeof numberedDocumentTypes)[number];

/**
 * The longest number Business Central takes in the Invoice Number of the invoices file (§7.4.2 #2). TODO(V-19): the
 * hypothesis of T-80, 20 characters, until Memento confirms it.
 */
export const FISCAL_NUMBER_MAX_LENGTH = 20;

/** The tokens of a pattern: the prefix of the series, the year of the document, the number in the series. */
const TOKEN = /\{(prefix|yyyy|yy|n)\}/g;

/**
 * A pattern: tokens and the characters a number of Business Central takes (letters, digits, `/`, `-`, `_`, `.`), with
 * the number once and the year at least once, so that the numbers of two years never meet. No `;`: the invoices file
 * is separated by it (§7.4.1).
 */
export const numberPattern = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .regex(/^(?:[A-Za-z0-9/_.-]|\{prefix\}|\{yyyy\}|\{yy\}|\{n\})+$/)
  .refine((pattern) => pattern.split("{n}").length === 2, { message: "the number once" })
  .refine((pattern) => /\{yyyy\}|\{yy\}/.test(pattern), { message: "the year at least once" });

/** A prefix of a series: what a number of Business Central takes, without the characters of the tokens. */
export const numberPrefix = z
  .string()
  .trim()
  .min(1)
  .max(10)
  .regex(/^[A-Za-z0-9/_.-]+$/);

/** How a series writes its numbers. */
export interface NumberFormat {
  prefix: string | null;
  pattern: string;
  /** The least digits of the number, padded with zeros. */
  digits: number;
}

/** The number `n` of a series in the year of its document, e.g. `INV2026-000123`. */
export function formatDocumentNumber(format: NumberFormat, year: number, n: number): string {
  const values: Record<string, string> = {
    prefix: format.prefix ?? "",
    yyyy: String(year),
    yy: String(year % 100).padStart(2, "0"),
    n: String(n).padStart(format.digits, "0"),
  };
  return format.pattern.replace(TOKEN, (_, token: string) => values[token] ?? "");
}
